import React, { useCallback, useMemo, useRef } from "react";
import classNames from "classnames/bind";
import styles from "./CategoryFilters.module.scss";
import { FilterButtons } from "../../../../components/FilterButtons";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "../../../../hooks/useSearchParams";
import { AdditionalFeature, ProductsQueryParams } from "@core/types";

const cx = classNames.bind(styles);

interface CategoryFiltersProps {
  categoryId?: string;
  categoriesFilters: AdditionalFeature[];
}

const CategoryFilters = ({ categoryId, categoriesFilters }: CategoryFiltersProps) => {
  const queryClient = useQueryClient();
  const scrollContainedRef = useRef<HTMLDivElement>(null);
  const { searchParams, set, delete: deleteParam } = useSearchParams();
  const urlCategory = searchParams.get("additionalProductFilter");

  const handleCategoryClick = useCallback(
    async (name: string) => {
      // * Если количество категорий больше 3, то прокручиваем контейнер
      if (categoriesFilters.length > 3) {
        scrollContainedRef.current?.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      }

      // * Если нажата уже выбранная категория, то удаляем параметр и инвалидируем запрос
      if (name === urlCategory) {
        deleteParam("additionalProductFilter");
        await queryClient.invalidateQueries([
          "products",
          {
            queryKey: [
              "products",
              {
                additionalProductFilter: name,
                categoryId: categoryId,
              } as ProductsQueryParams,
            ],
          },
        ]);
        return;
      }

      set("additionalProductFilter", name);

      await queryClient.invalidateQueries([
        "products",
        {
          queryKey: [
            "products",
            {
              additionalProductFilter: name,
              categoryId: categoryId,
            } as ProductsQueryParams,
          ],
        },
      ]);
    },
    [urlCategory, categoriesFilters.length, categoryId, deleteParam, queryClient, set],
  );

  const sortedCategories = useMemo(() => {
    if (!urlCategory) return categoriesFilters;

    return [
      ...categoriesFilters.filter(({ additionalFeature }) => additionalFeature === urlCategory),
      ...categoriesFilters.filter(({ additionalFeature }) => additionalFeature !== urlCategory),
    ];
  }, [urlCategory, categoriesFilters]);

  return (
    <div
      ref={scrollContainedRef}
      className={cx("hiddenScrollbar", "flex scroll-smooth overflow-x-auto gap-2 pl-5 pr-5")}
    >
      {sortedCategories.map(({ additionalFeature }, index) => {
        const isActive = urlCategory === additionalFeature;
        return (
          <div
            key={index}
            className={cx(
              "flex-shrink-0",
              isActive && "flex rounded-[1.75rem] pr-2 bg-[#649745] text-white items-center",
            )}
          >
            <FilterButtons filterName={additionalFeature} handleFilterClick={handleCategoryClick} isActive={isActive} />
          </div>
        );
      })}
    </div>
  );
};

CategoryFilters.displayName = "Parhato2_CategoryFilters";

export { CategoryFilters };

export default CategoryFilters;
